// กระดาษคำตอบหนึ่งแผ่น วาดเป็น SVG หน่วยมิลลิเมตร เพื่อให้ตำแหน่งที่พิมพ์ออกมาตรงกับ sheetLayout.js
// (ตัวอ่านกระดาษในขั้นสแกนใช้ตัวเลขชุดเดียวกัน) — ห้ามจัดตำแหน่งด้วย CSS/flex ในนี้
import React from 'react'
import { MARKERS, PAGE, LAYOUT_VERSION, codeCells, bubbleLayout } from './sheetLayout'
import { encodeSheetCode, sheetCodeText } from './sheetCode'

const FONT = "'THSarabun', 'Sarabun', sans-serif"

const Text = ({ x, y, size = 4.2, weight, anchor, fill = '#000', children }) => (
  <text x={x} y={y} fontSize={size} fontFamily={FONT} fontWeight={weight} textAnchor={anchor} fill={fill}>
    {children}
  </text>
)

// ช่วงเลขข้อต่อเนื่อง [1,2,3,5] -> "1–3, 5"
const ranges = (nums) => {
  const out = []
  let start = null
  let prev = null
  for (const n of nums) {
    if (start === null) start = n
    else if (n !== prev + 1) {
      out.push(start === prev ? `${start}` : `${start}–${prev}`)
      start = n
    }
    prev = n
  }
  if (start !== null) out.push(start === prev ? `${start}` : `${start}–${prev}`)
  return out.join(', ')
}

export default function AnswerSheet({ exam, questions, student, room, school }) {
  const rows = bubbleLayout(questions, exam.exam_choices)
  const bits = encodeSheetCode(exam.exam_id, student.study_id)
  const cells = codeCells()
  const written = questions.filter((q) => q.eq_type === 'written').map((q) => q.eq_no)
  const hasTF = questions.some((q) => q.eq_type === 'truefalse')

  return (
    <svg
      className="exam-sheet"
      xmlns="http://www.w3.org/2000/svg"
      width={`${PAGE.width}mm`}
      height={`${PAGE.height}mm`}
      viewBox={`0 0 ${PAGE.width} ${PAGE.height}`}
    >
      <rect x="0" y="0" width={PAGE.width} height={PAGE.height} fill="#fff" />

      {MARKERS.map((m) => (
        <rect key={m.corner} x={m.x} y={m.y} width={m.size} height={m.size} fill="#000" />
      ))}

      {/* หัวกระดาษ */}
      {school.logoSrc && <image href={school.logoSrc} x="22" y="9" width="16" height="16" preserveAspectRatio="xMidYMid meet" />}
      <Text x={42} y={14} size={5} weight="bold">{school.school_name}</Text>
      <Text x={42} y={20} size={5.4} weight="bold">กระดาษคำตอบ: {exam.exam_name}</Text>
      <Text x={42} y={25.5} size={4.4}>
        วิชา {room.sub_subid} {room.sub_subname} · ภาคเรียนที่ {room.sub_semester}/{room.year_year}
      </Text>

      {/* ข้อมูลนักเรียน — พิมพ์ให้แล้ว นักเรียนไม่ต้องเขียน */}
      <rect x="18" y="30" width="174" height="15" rx="1.5" fill="none" stroke="#000" strokeWidth="0.3" />
      <Text x={22} y={36.5} size={4.6}>ชื่อ-สกุล</Text>
      <Text x={38} y={36.5} size={5} weight="bold">{student.st_name}</Text>
      <Text x={22} y={42.5} size={4.6}>เลขประจำตัว</Text>
      <Text x={42} y={42.5} size={5} weight="bold">{student.st_idnumber}</Text>
      <Text x={90} y={42.5} size={4.6}>ห้อง</Text>
      <Text x={98} y={42.5} size={5} weight="bold">ม.{room.c_id}/{room.r_no}</Text>
      <Text x={130} y={42.5} size={4.6}>เลขที่</Text>
      <Text x={140} y={42.5} size={5} weight="bold">{student.seat}</Text>

      {/* แถบรหัสแผ่น */}
      <Text x={PAGE.width / 2} y={50} size={3.6} anchor="middle" fill="#444">
        รหัสแผ่น — ห้ามขีดเขียนหรือพับบริเวณนี้
      </Text>
      {cells.map((c) => (
        <rect
          key={c.index}
          x={c.x}
          y={c.y}
          width={c.size}
          height={c.size}
          fill={bits[c.index] ? '#000' : 'none'}
          stroke="#000"
          strokeWidth="0.2"
        />
      ))}
      <Text x={PAGE.width / 2} y={66.5} size={3.4} anchor="middle" fill="#444">
        {sheetCodeText(exam.exam_id, student.study_id)} · แบบ {LAYOUT_VERSION}
      </Text>

      {/* คำชี้แจง */}
      <Text x={18} y={72} size={4}>
        ฝนหรือกากบาทให้เต็มวงเพียงวงเดียวต่อข้อ ถ้าต้องการเปลี่ยนคำตอบให้ลบให้สะอาด
        {hasTF ? ' · ข้อใช่/ไม่ใช่: ถ = ใช่/ถูก, ผ = ไม่ใช่/ผิด' : ''}
      </Text>

      {/* ช่องฝน */}
      {rows.map((row) => (
        <g key={row.eq_no}>
          <Text x={row.numberX} y={row.cy + 1.4} size={4.2} weight="bold" anchor="end">{row.eq_no}</Text>
          {row.bubbles.map((b) => (
            <g key={b.value}>
              <circle cx={b.cx} cy={b.cy} r={b.r} fill="none" stroke="#000" strokeWidth="0.25" />
              <Text x={b.cx} y={b.cy + 1.1} size={3} anchor="middle" fill="#999">{b.label}</Text>
            </g>
          ))}
        </g>
      ))}

      {written.length > 0 && (
        <Text x={18} y={PAGE.height - 12} size={4}>
          ข้อ {ranges(written)} เป็นอัตนัย ตอบในกระดาษแยก
        </Text>
      )}
    </svg>
  )
}
